
import cloneDeep from "lodash-es/cloneDeep";
import get from "lodash-es/get";
//import find from "lodash-es/find";
//import map from "lodash-es/map";
//import every from "lodash-es/every";
import sortBy from "lodash-es/sortBy";
import last from "lodash-es/last";
//import { Memoize } from "lodash-decorators";

import "es6-promise";

import axios, { AxiosResponse } from "axios";
import Vue, { ComponentOptions } from "vue";
import Component from "vue-class-component";
import {
  Emit,
  Inject,
  Model,
  Prop,
  Provide,
  Watch
} from "vue-property-decorator";

import { compareVersions } from 'compare-versions';

import { Get, Sync, Call } from "vuex-pathify";

import { SET_SAMPLES, CLEAR_SAMPLE_CART } from "../../../../store";

import storeModule from "../store/module";

import { Sample, SampleCartItems } from "../../../../model";
import { WebAPI } from "../../../../web-api";

import { Snackbar } from "../../../../snackbar";
import BannerNotice from "../../../BannerNotice.vue";
import InputFilesForm from "../../../InputFilesForm.vue";
import SelectGenome from "../../../SelectGenome.vue";
import { ReferenceGenome } from "../../../../types";

@Component({
  components: {
    BannerNotice,
    InputFilesForm,
    SelectGenome
  },
  props: {},
  filters: {},
  beforeRouteLeave(to: any, from: any, next: any) {
    (this as any).beforeRouteLeave(to, from, next);
  }
})
export default class PipelineParams extends Vue {
  public pipeline_name: string = "nf-core-rnaseq";
  public available_genomes: Array<ReferenceGenome> | null = null;

  @Prop({ default: true, type: Boolean })
  public showButtons: boolean;
  public show_advanced = false;

  public submitting: boolean = false;
  public error_alert_message: string = "Everything is fine. 🏩";
  public snackbar_message: string = "Everything is fine. ௐ";
  public snackbar_duration: number = 2000;

  public pipelinerun_uuid: string | null = null;
  public selectedSamples: Array<Sample> = [];

  get pipeline_versions() {
    // const versions = = ["3.1", "3.2", "3.10.1"];
    return get(
      this.$store.state.availablePipelines[this.pipeline_name],
      "metadata.versions",
      []
    );
  }

  get default_pipeline_version() {
    const version = get(
      this.$store.state.availablePipelines[this.pipeline_name],
      "metadata.default_version",
      last(
        sortBy(this.pipeline_versions, v => {
          return v;
        })
      )
    );
    // For older versions, ensure the strandedness dropdown is populated
    // with a default that is not 'auto'.
    if (compareVersions(version, "3.7") < 0) {
      this.strandedness = 'unstranded';
    }
    return version;
  }

  get standedness_options() {
    if (this.auto_strandedness_option_supported) {
      return [
        { value: 'auto', text: 'auto' },
        { value: 'unstranded', text: 'unstranded' },
        { value: 'forward', text: 'forward' },
        { value: 'reverse', text: 'reverse' }
      ]
    };

    return [
      { value: 'unstranded', text: 'unstranded' },
      { value: 'forward', text: 'forward' },
      { value: 'reverse', text: 'reverse' }
    ]
  }

  get trimmer_options() {
    return [
      { value: 'fastp', text: 'fastp' },
      { value: 'trimgalore', text: 'trimgalore' }
    ]
  }

  public _samples: SampleCartItems;
  get samples(): SampleCartItems {
    this._samples = cloneDeep(this.$store.state.samples);
    // We trigger the computed property so that if samples change validation runs
    const _ = this.isValid_params;
    return this._samples;
  }

  @Sync("pipelineParams@description")
  public description: string;

  @Sync("pipelineParams@pipeline_version")
  public pipeline_version: string;

  @Sync("pipelineParams@nf-core-rnaseq.strandedness")
  public strandedness: string;

  @Sync("pipelineParams@nf-core-rnaseq.trimmer")
  public trimmer: string;

  @Sync("pipelineParams@nf-core-rnaseq.debug_mode")
  public debug_mode: boolean;

  @Sync("pipelineParams@nf-core-rnaseq.has_umi")
  public has_umi: boolean;

  @Sync("pipelineParams@nf-core-rnaseq.min_mapped_reads")
  public min_mapped_reads: number;

  @Sync("pipelineParams@nf-core-rnaseq.save_reference_genome")
  public save_reference_genome: boolean;

  @Sync("pipelineParams@nf-core-rnaseq.save_genome_index")
  public save_genome_index: boolean;

  @Sync("pipelineParams@nf-core-rnaseq.skip_trimming")
  public skip_trimming: boolean;

  @Sync("pipelineParams@nf-core-rnaseq.skip_alignment")
  public skip_alignment: boolean;

  created() {
    this.$store.registerModule(
      ["pipelineParams", this.pipeline_name],
      storeModule
    );

    this._samples = cloneDeep(this.$store.state.samples);
    this.pipeline_version = this.default_pipeline_version;
  }

  /*
   *  Populates the pipelineParams.fetch_files list with files
   *  from the sample cart (and external genome reference files)
   *  that should be retrieved by the backend as initial input files.
   */

  updateFetchFiles() {
    if (!this.$store.state.use_custom_genome) {
      this.$store.set("pipelineParams@user_genome.fasta_url", "");
      this.$store.set("pipelineParams@user_genome.annotation_url", "");
    }

    const fetch_files = this.$store.get(
      "pipelineParams/generateFetchFilesList"
    );
    let params = this.$store.copy("pipelineParams");
    params.fetch_files = fetch_files;
    this.$store.set("pipelineParams", params);
  }

  prepareData() {
    this.updateFetchFiles();

    let data = {
      sample_cart: this.$store.state.samples.id,
      params: this.$store.getters.pipelineParams,
      pipeline: this.pipeline_name,
      description: this.description
    };

    data = cloneDeep(data);

    if (this.$store.state.use_custom_genome) {
      data.params.genome = null;
    }

    // If skipping trimming, set the trimmer param to blank
    if (this.skip_trimming) {
      data.params['nf-core-rnaseq'].trimmer = '';
    }

    return data;
  }

  get isValid_samples_added() {
    return this.$store.getters.sample_cart_count > 0;
  }

  get isValid_duplicate_samples() {
    const samples = this.$store.state.samples;
    const seen: string[] = [];
    for (let i of samples.items) {
      for (let f of i.files) {
        for (let pair of ["R1", "R2"]) {
          if (f[pair] == null) continue;
          if (f[pair].location == null) continue;
          if (seen.includes(f[pair].location)) return false;
          seen.push(f[pair].location);
        }
      }
    }

    return true;
  }

  get isValid_reference_genome() {
    return this.$store.get("pipelineParams/isValidReferenceGenome");
  }

  get trimmer_option_supported() {
    let supported_versions = ['3.18.0', '3.19.0'];
    return supported_versions.includes(this.pipeline_version);
  }

  get auto_strandedness_option_supported() {
    return compareVersions(this.pipeline_version, "3.7") >= 0;
  }

  get umi_option_supported() {
    // nf-core/rnaseq had some UMI support in earlier versions (at least since 3.2),
    // however options were limited - we only support more recent versions here
    const supported = compareVersions(this.pipeline_version, "3.10") >= 0;
    if (!supported) {
      this.has_umi = false;
    }
    return supported;
  }

  get isValid_strandedness_option() {
    if (this.strandedness == 'auto') {
      // version 3.7+ required to support the 'auto' option
      return this.auto_strandedness_option_supported;
    }
    return true;
  }

  get isValid_min_mapped_reads() {
    if (this.min_mapped_reads >= 0 &&
      this.min_mapped_reads <= 100) {
      return true;
    }
    return false;
  }

  get isValid_params() {
    let is_valid = false;
    if (
      this.isValid_reference_genome &&
      this.isValid_samples_added &&
      this.isValid_duplicate_samples &&
      this.isValid_strandedness_option &&
      this.isValid_min_mapped_reads
    ) {
      is_valid = true;
    }
    this.$store.set("pipelineParams_valid", is_valid);

    return is_valid;
  }

  async save() {
    try {
      this.submitting = true;
      await this.$store.dispatch(SET_SAMPLES, this._samples);

      const data = this.prepareData();
      // console.log(data);

      if (this.pipelinerun_uuid == null) {
        const response = (await WebAPI.fetcher.post(
          "/api/v1/pipelinerun/",
          data
        )) as AxiosResponse;
        this.pipelinerun_uuid = response.data.id;
      } else {
        (await WebAPI.fetcher.put(
          `/api/v1/pipelinerun/${this.pipelinerun_uuid}/`,
          data
        )) as AxiosResponse;
      }
      this.submitting = false;
      Snackbar.flashMessage("Saved !");
    } catch (error) {
      console.log(error);
      this.submitting = false;
      this.error_alert_message = error.toString();
      this.openDialog("error_dialog");
    }
  }

  async run() {
    try {
      this.submitting = true;
      await this.save();
      this.submitting = true;

      if (!this.isValid_params) {
        Snackbar.flashMessage("Please correct errors before submitting.");
        this.submitting = false;
        return null;
      }

      try {
        let response = null;
        response = (await WebAPI.fetcher.post(
          `/api/v1/job/?pipeline_run_id=${this.pipelinerun_uuid}`,
          {}
        )) as AxiosResponse;
        this.submitting = false;
        Snackbar.flashMessage("Saved !");
        await this.clearCart();

        if (response == null) {
          throw Error("Request to start job failed");
        }
        if (response && response.data && response.data.id) {
          this.$router.push({
            name: "job",
            params: { jobId: response.data.id }
          });
        }
        // } else {
        //   this.$router.push("jobs");
        // }

        return response;
      } catch (error) {
        console.log(error);
        this.submitting = false;
        this.error_alert_message = error.toString();
        this.openDialog("error_dialog");
      }
    } catch (error) {
      console.error(error);
    }

    this.submitting = false;
    return null;
  }

  async clearCart() {
    await this.$store.dispatch(CLEAR_SAMPLE_CART);
  }

  openDialog(ref: string) {
    (this.$refs[ref] as MdDialog).open();
  }

  closeDialog(ref: string) {
    (this.$refs[ref] as MdDialog).close();
  }

  closeSnackbar() {
    const snackbar = this.$refs.snackbar as any;
    if (snackbar && typeof snackbar.close === "function") {
      snackbar.close();
    }
  }

  onSelect(rows: any) {
    this.selectedSamples = rows as Array<Sample>;
  }

  routeTo(name: string, params: any = {}) {
    this.$router.push({ name: name, params: params });
  }

  @Watch("pipeline_version", { immediate: true })
  onPipelineVersionChange(newVersion: string, oldVersion: string) {
    if (!this.trimmer_option_supported) {
      this.trimmer = "";
    } else if (this.trimmer === "") {
      this.trimmer = "fastp";
    }

    if (newVersion && compareVersions(newVersion, "3.7") < 0 && this.strandedness === 'auto') {
      this.strandedness = 'unstranded';
    }
  }

  beforeRouteLeave(to: any, from: any, next: any) {
    // console.log([to, from, next]);
    this.$store.set("samples", this._samples);
    next();
  }
}
